/**
 * SSO Login Page
 * Handles SSO authentication from One Manager Platform
 *
 * URL: /sso?token=<sso_token>&attempt=<n>&attempts=<max> (hub docs/04 §3)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { authAPI } from '../api/auth';
import { PLATFORM_HOME, PLATFORM_SUPPORT, reopenUrl, tenantOfLink } from '../utils/hubSession';
import { usePlatformName } from '../contexts/PlatformBrandingContext';

// Use the base API URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
// People who come through the hub have no password here, so a failed sign-in offers to try again
// through One Manager, then its support page, never this app's login page.

// The API sleeps when unused (Render's free plan) and can take a couple of minutes to wake
const SIGN_IN_TIMEOUT_MS = 150_000;
const WAKING_AFTER_MS = 8_000;

// Refusals that trying again won't change: the server's message says what the person can do
const FINAL_CODES = ['BMS_ACCESS_CLOSED', 'EMAIL_IN_USE', 'EMAIL_NOT_VERIFIED', 'PLAN_LIMIT_REACHED'];

/** What went wrong, in words the person can act on (the server's details stay in its logs). */
function failureOf(status, data) {
  if (FINAL_CODES.includes(data?.code) && data.error) return { message: data.error, retryable: false };
  if (status === 401) {
    return { message: 'This sign-in link has expired or was already used.', retryable: true };
  }
  return { message: "The BMS couldn't finish signing you in.", retryable: true };
}

/** The attempt this is, and how many One Manager allows (the admin hub sets it; 3 if not said). */
function attemptsOf(searchParams) {
  const attempts = Math.min(Math.max(parseInt(searchParams.get('attempts'), 10) || 3, 1), 10);
  const attempt = Math.min(Math.max(parseInt(searchParams.get('attempt'), 10) || 1, 1), attempts);
  return { attempt, attempts };
}

export default function SsoLoginPage() {
  const platformName = usePlatformName();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ssoLogin, updateUser } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Authenticating...');
  const ssoToken = searchParams.get('token');
  const { attempt, attempts } = attemptsOf(searchParams);

  useEffect(() => {
    if (!ssoToken) {
      setError({ message: `This page needs a sign-in link from ${platformName}.`, retryable: false });
      return;
    }

    handleSsoLogin(ssoToken);
  }, [searchParams]);

  const handleSsoLogin = async (ssoToken) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SIGN_IN_TIMEOUT_MS);
    const waking = setTimeout(
      () => setStatus('The BMS is waking up. This can take a minute or two...'),
      WAKING_AFTER_MS
    );
    try {
      setStatus('Validating SSO token...');

      // Call BMS SSO login endpoint directly (not through the API client which may add interceptors)
      const response = await fetch(`${API_URL}/sso/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ssoToken }),
        signal: controller.signal
      });

      const data = await response.json();

      if (data.success) {
        setStatus('Login successful! Redirecting...');

        // Build user object compatible with BMS
        const user = {
          id: data.user.id,
          email: data.user.email,
          role: data.user.role,
          full_name: data.user.full_name || data.user.email,
          tenantRole: data.user.tenantRole,
          modules: data.user.modules || {},
          moduleConfig: data.user.moduleConfig || {},
          limits: data.user.limits || {},
          is_sso: true
        };

        // A module chosen in an earlier session (perhaps another business or person) doesn't carry over
        localStorage.removeItem('activeModule');
        ssoLogin(data.accessToken, user, data.tenant);
        // Their role's permissions decide the modules and pages they can open
        try {
          const me = await authAPI.getCurrentUser();
          if (me?.data) updateUser({ ...user, ...me.data, is_sso: true, tenantRole: user.tenantRole });
        } catch {
          // the next page load reads them again; until then the role name decides
        }

        // The module selection page sends them on: their role and the plan decide the modules,
        // with a choice when there is more than one
        setTimeout(() => {
          navigate('/select-module', { replace: true });
        }, 1000);
      } else {
        setError(failureOf(response.status, data));
      }
    } catch (err) {
      console.error('SSO login error:', err);
      setError({
        message:
          err.name === 'AbortError'
            ? 'The BMS took too long to answer.'
            : "The BMS can't be reached right now.",
        retryable: true
      });
    } finally {
      clearTimeout(timeout);
      clearTimeout(waking);
    }
  };

  if (error) {
    // Try again through One Manager (a fresh link each time) up to the number it allows, then
    // its support page
    const retryUrl = error.retryable && attempt < attempts ? reopenUrl(tenantOfLink(ssoToken || ''), attempt + 1) : null;
    const giveUp = !retryUrl && PLATFORM_SUPPORT;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Couldn&apos;t sign you in</h2>
          <p className="text-gray-600 mb-2">{error.message}</p>
          <p className="text-gray-600 mb-6">
            {retryUrl
              ? 'Try again: the BMS may just have been slow to start.'
              : error.retryable
                ? "It still didn't work after several tries. Contact support and we'll sort it out."
                : 'If you think this is a mistake, contact support.'}
          </p>
          {retryUrl && (
            <>
              <a
                href={retryUrl}
                className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Try again
              </a>
              <p className="text-xs text-gray-500 mt-2">
                Attempt {attempt} of {attempts}
              </p>
            </>
          )}
          {giveUp && (
            <a
              href={PLATFORM_SUPPORT}
              className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Contact support
            </a>
          )}
          {PLATFORM_HOME && (
            <a
              href={PLATFORM_HOME}
              className={
                retryUrl || giveUp
                  ? 'block mt-4 text-sm text-blue-700 hover:text-blue-900'
                  : 'block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
              }
            >
              Back to {platformName}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        <LoadingSpinner />
        <h2 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Single Sign-On</h2>
        <p className="text-gray-600">{status}</p>
        <p className="text-sm text-gray-400 mt-4">Signing you in through {platformName}...</p>
      </div>
    </div>
  );
}
